// CardOnFileModal.jsx — "we need a card to hold this appointment".
//
// Owner directive 2026-09-01: a card must be taken for EVERY appointment, and
// the prompt has to appear no matter how the booking was made — including the
// "+ New Client" flow, which previously skipped it entirely (test safa, 16:05).
//
// Opens right after an appointment is saved when the client has no card yet.
// Three ways out, so the front desk is never stuck:
//   1. Take the card now — staff types it while the client is on the phone or
//      at the desk (Stripe SetupIntent; nothing is charged today).
//   2. Text the secure link — the client saves it herself in under a minute.
//   3. Skip this time — a deliberate waiver, stamped on the appointment.
import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "./supabase";

// supabase-js hides a function's real error behind "Edge Function returned a
// non-2xx status code". Dig the actual message out of the response body so the
// front desk sees WHY (e.g. "Your card was declined") instead of a riddle.
async function realError(error, data, fallback) {
  if (data?.error) return String(data.error);
  try {
    if (error?.context?.json) {
      const j = await error.context.json();
      if (j?.error) return String(j.error);
    } else if (error?.context?.text) {
      const t = await error.context.text();
      if (t) return t.slice(0, 300);
    }
  } catch (_) { /* fall through */ }
  return error?.message || fallback;
}

const stripePromise = loadStripe("pk_live_51TPSNN9gc0MT8Jg7aFesfPT0xlWbQ5fx1kHIpHqw1nwna52Z0sai0h5L0nHbQfO9kRII1On3Ag4ikZwO6zCJ9vuc00FIGFrxoh");

const C = {
  card:"#ffffff", border:"#e8e4df", borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e", green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

function CardEntryForm({ clientId, onSaved, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!stripe || !elements) return;
    setBusy(true);
    onError("");
    try {
      const { error: confirmErr, setupIntent } = await stripe.confirmSetup({
        elements, redirect: "if_required", confirmParams: {},
      });
      if (confirmErr) { onError(confirmErr.message || "Card was declined or invalid"); setBusy(false); return; }
      const pmId = setupIntent?.payment_method;
      if (!pmId) { onError("Stripe returned no card — please try again"); setBusy(false); return; }
      const { data, error } = await supabase.functions.invoke("stripe-membership", {
        body: { action: "save_payment_method", client_id: clientId, payment_method_id: pmId, set_default: true },
      });
      if (error || data?.error) {
        onError(await realError(error, data, "Could not save the card"));
        setBusy(false);
        return;
      }
      onSaved(data?.saved?.card_last4 || null);
    } catch (err) {
      onError(err?.message || String(err));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement options={{ terms: { card: "never" } }} />
      <button type="submit" disabled={busy || !stripe} style={{width:"100%",marginTop:14,padding:"12px",fontSize:14,fontWeight:700,background:busy?C.light:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:busy?C.muted:"#fff",border:"none",borderRadius:9,cursor:busy?"not-allowed":"pointer",fontFamily:F}}>
        {busy ? "Saving…" : "🔒 Save card (nothing charged)"}
      </button>
    </form>
  );
}

export default function CardOnFileModal({ client, appointment, onDone }) {
  const [mode, setMode] = useState("choose");   // choose | entering | done
  const [clientSecret, setClientSecret] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedLast4, setSavedLast4] = useState(null);

  const firstName = (client?.name || "there").trim().split(/\s+/)[0];

  const startCardEntry = async () => {
    setBusy(true); setErr("");
    try {
      const { data, error } = await supabase.functions.invoke("stripe-membership", {
        body: { action: "create_setup_intent", client_id: client.id },
      });
      if (error || data?.error) { setErr(await realError(error, data, "Could not open the card form")); return; }
      if (!data?.client_secret) { setErr("Could not open the card form — please try again"); return; }
      setClientSecret(data.client_secret);
      setMode("entering");
    } catch (e) {
      setErr(e?.message || "Could not open the card form");
    } finally { setBusy(false); }
  };

  const textLink = async () => {
    if (!client?.phone) { setErr("This client has no phone on file — add one, or take the card here."); return; }
    setBusy(true); setErr("");
    const link = `https://www.elyonhq.com/card?cid=${client.id}&n=${encodeURIComponent(firstName)}`;
    const msg = `Hi ${firstName}, it's Nova Med Spa 💛 To hold your appointment, please save a card on file — you won't be charged today. Free cancellation up to 24 hours before your visit; changes within 24h are $25 (credited to your treatment); no-shows $50. Secure link: ${link}`;
    const { data, error } = await supabase.functions.invoke("send-sms", {
      body: { to: client.phone, message: msg, sender: "system", source: "card_guarantee" },
    });
    setBusy(false);
    if (error || data?.ok === false) { setErr("SMS failed: " + (error?.message || data?.error || data?.skipped || "unknown")); return; }
    setMode("done");
    setSavedLast4("sms");
  };

  const skip = async () => {
    setBusy(true);
    try {
      if (appointment?.id) {
        const { data: { user } } = await supabase.auth.getUser();
        const { data: prof } = user ? await supabase.from("profiles").select("full_name,email").eq("id", user.id).maybeSingle() : { data: null };
        await supabase.from("appointments").update({
          card_requirement_waived: true,
          card_waived_by_name: prof?.full_name || prof?.email || "staff",
        }).eq("id", appointment.id);
      }
    } catch (e) { console.warn("waiver stamp failed:", e); }
    setBusy(false);
    onDone({ skipped: true });
  };

  const btn = (bg, color, border) => ({ padding:"11px 14px",fontSize:13,fontWeight:700,background:bg,color,border:border||"none",borderRadius:9,cursor:busy?"not-allowed":"pointer",fontFamily:F,width:"100%" });

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:5000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(3px)"}}>
      <div style={{background:C.card,borderRadius:14,width:"100%",maxWidth:440,padding:24,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.35)"}}>
        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>💳 Card to Hold This Appointment</div>
        <div style={{fontSize:12.5,color:C.muted,marginBottom:16}}>
          {client?.name} · {appointment?.treat || "Appointment"} · {appointment?.date} {String(appointment?.time || "").slice(0,5)}
        </div>

        {mode === "done" ? (
          <>
            <div style={{padding:"16px",background:C.greenL,border:`1px solid ${C.green}50`,borderRadius:10,marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:700,color:C.green}}>
                {savedLast4 === "sms" ? "✅ Card link texted" : `✅ Card saved${savedLast4 ? ` (•••• ${savedLast4})` : ""}`}
              </div>
              <div style={{fontSize:12,color:C.muted,marginTop:4}}>
                {savedLast4 === "sms"
                  ? `${firstName} can save her card in under a minute. The appointment is booked either way.`
                  : "Nothing was charged. The cancellation policy now applies to this appointment."}
              </div>
            </div>
            <button onClick={()=>onDone({ saved: true })} style={btn(`linear-gradient(135deg,${C.gold},${C.goldD})`,"#fff")}>Done</button>
          </>
        ) : mode === "entering" && clientSecret ? (
          <>
            <div style={{padding:"10px 12px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:8,fontSize:12,color:C.gold,marginBottom:14}}>
              Nothing is charged today. $25 applies only to changes within 24h (credited to her treatment); $50 to a no-show.
            </div>
            <Elements stripe={stripePromise} options={{ clientSecret, appearance:{ variables:{ colorPrimary: C.gold } } }}>
              <CardEntryForm clientId={client.id} onSaved={(l4)=>{setSavedLast4(l4);setMode("done");}} onError={setErr} />
            </Elements>
            <button onClick={()=>setMode("choose")} style={{...btn(C.light,C.muted,`1px solid ${C.border}`),marginTop:10}}>← Back</button>
          </>
        ) : (
          <>
            <div style={{padding:"10px 12px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:8,fontSize:12,color:C.gold,marginBottom:14,lineHeight:1.5}}>
              Free cancellation up to 24 hours before · $25 for changes within 24h (credited to her treatment) · $50 no-show. <b>Nothing is charged today.</b>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:9}}>
              <button onClick={startCardEntry} disabled={busy} style={btn(`linear-gradient(135deg,${C.gold},${C.goldD})`,"#fff")}>
                {busy ? "Opening…" : "💳 Take card now (she's here / on the phone)"}
              </button>
              <button onClick={textLink} disabled={busy} style={btn(C.goldBg,C.gold,`1px solid ${C.borderGold}`)}>
                📲 Text her the secure link
              </button>
              <button onClick={skip} disabled={busy} style={btn(C.light,C.muted,`1px solid ${C.border}`)}>
                Skip this time (waive — recorded)
              </button>
            </div>
          </>
        )}

        {err && <div style={{marginTop:12,padding:10,background:"rgba(192,57,43,0.08)",border:`1px solid ${C.red}40`,borderRadius:8,color:C.red,fontSize:12.5}}>{err}</div>}
        <div style={{marginTop:14,fontSize:10.5,color:C.dim,textAlign:"center"}}>🔒 Card details go straight to Stripe — Elyon never stores the number</div>
      </div>
    </div>
  );
}
